import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { validateResponse } from "@/api/validateResponse";
import { NewLeadsResponseSchema, type NewLead } from "@/api/types/newLead";

export const newLeadsKey = ["inquiries", "new-leads"] as const;

/**
 * GET /api/v2/inquiries/new-leads — polls for inquiries still sitting in
 * stage "new" so LEAD-2's global banner/badge/toast can make a fresh
 * website submission impossible to miss (see PourCRM ticket LEAD-2).
 *
 * Polls every 20s and also refetches on window focus, so a lead that
 * arrived while the tab was backgrounded surfaces the moment Chris tabs
 * back in. `count` naturally drops to 0 once every "new" lead has been
 * moved to a later stage — that's the backend's only "seen" signal.
 */
export function useNewLeads() {
  const query = useQuery({
    queryKey: newLeadsKey,
    queryFn: async () => {
      const { data } = await apiClient.get("/v2/inquiries/new-leads");
      return validateResponse(
        NewLeadsResponseSchema,
        data,
        "GET /v2/inquiries/new-leads",
      );
    },
    refetchInterval: 20_000,
    refetchOnWindowFocus: true,
    staleTime: 10_000,
  });

  const leads = query.data?.leads ?? [];
  const count = query.data?.count ?? 0;
  const latest: NewLead | undefined = leads[0];

  return { ...query, count, leads, latest };
}
